import React, { memo, MouseEvent, useCallback, useMemo, useState } from 'react'
import { ExportMinor, CirclePlusOutlineMinor, CirclePlusMinor } from '@shopify/polaris-icons'
import { ActionButton } from 'src/components/shared/ActionButton'
import { ActionMenu } from 'src/components/shared/ActionMenu'
import { FundingInvoiceStatus, FundingType } from 'src/constants/payment/enums'
import { useModalOpen } from 'src/hooks/useModalOpen'
import { MenuActionItem } from 'src/interfaces/ui'
import tw from 'twin.macro'
import { PaymentInProgressModal } from './PaymentInProgressModal'
import { FundingInvoice } from 'src/interfaces/payment'
import { useSelector } from 'react-redux'
import { getAccessInfo } from 'src/selectors/user'
import { UserProductName } from 'src/constants/user'

interface Props {
  currentInvoice: FundingInvoice | null
  onOpenBankFundInProgress(): void
  onOpenInstantFundInProgress(): void
  onOpenUploadPaymentsModal(): void
  onOpenAddSinglePaymentModal(): void
}

const AddPaymentsActionsButtonComponent = ({
  currentInvoice,
  onOpenBankFundInProgress,
  onOpenInstantFundInProgress,
  onOpenUploadPaymentsModal,
  onOpenAddSinglePaymentModal,
}: Props) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

  const accessInfo = useSelector(getAccessInfo)

  const {
    isOpen: isPaymentInProgressOpen,
    open: openPaymentInProgress,
    close: closePaymentInProgress,
  } = useModalOpen()

  const isMenuOpen = useMemo(() => Boolean(anchorEl), [anchorEl])

  const handleClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      if (currentInvoice?.status === FundingInvoiceStatus.Pending) {
        openPaymentInProgress()

        return
      }

      setAnchorEl(event.currentTarget)
    },
    [currentInvoice, openPaymentInProgress],
  )

  const handleGoToDetails = useCallback(() => {
    closePaymentInProgress()

    if (currentInvoice?.type === FundingType.Wire) {
      onOpenBankFundInProgress()
    }

    if (currentInvoice?.type === FundingType.Loan) {
      onOpenInstantFundInProgress()
    }
  }, [currentInvoice?.type])

  const handleCloseMenu = useCallback(() => {
    setAnchorEl(null)
  }, [])

  const actionItems: Array<MenuActionItem> = useMemo(() => {
    return [
      {
        label: 'Upload CSV file',
        icon: <ExportMinor width={20} height={20} />,
        onClick: async () => {
          setAnchorEl(null)

          onOpenUploadPaymentsModal()
        },
      },
      {
        label: 'Add single payment',
        icon: <CirclePlusOutlineMinor width={20} height={20} />,
        onClick: async () => {
          setAnchorEl(null)

          onOpenAddSinglePaymentModal()
        },
      },
    ]
  }, [onOpenUploadPaymentsModal, onOpenAddSinglePaymentModal])

  if (!actionItems.length) {
    return null
  }

  return (
    <>
      <ActionButton
        selected={!!anchorEl}
        color="white"
        text={
          accessInfo?.products?.some((item) => item.product === UserProductName.Subcontractor)
            ? 'Add contractor payments'
            : 'Add payments'
        }
        icon={<CirclePlusMinor width={20} height={20} />}
        onClick={handleClick}
      />
      <ActionMenu
        anchorEl={anchorEl}
        items={actionItems}
        isOpen={isMenuOpen}
        itemClassName={tw`min-w-[210px]`}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        onClose={handleCloseMenu}
      />
      <PaymentInProgressModal
        isOpen={isPaymentInProgressOpen}
        onClose={closePaymentInProgress}
        onGoToDetails={handleGoToDetails}
      />
    </>
  )
}

export const AddPaymentsActionsButton = memo(AddPaymentsActionsButtonComponent)
